(() => {
  const connection = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
  const prefetchedUrls = new Set();
  const scrollPositionKey = `preaScrollPosition:${window.location.pathname}${window.location.search}`;
  let pendingAnchor = null;
  let pendingTimer = null;
  let scrollSaveFrame = null;
  let scrollPositionFrozen = false;

  const shouldAvoidPrefetch =
    connection && (connection.saveData || /(^|-)2g$/.test(connection.effectiveType || ''));

  const eligibleProductUrl = (anchor) => {
    if (!anchor || anchor.hasAttribute('data-no-prefetch') || anchor.target === '_blank') return null;

    let url;
    try {
      url = new URL(anchor.href, window.location.href);
    } catch (error) {
      return null;
    }

    if (url.origin !== window.location.origin || !url.pathname.startsWith('/products/')) return null;
    if (url.searchParams.has('view')) return null;

    url.hash = '';
    return url.href;
  };

  const prefetchProduct = (anchor) => {
    if (shouldAvoidPrefetch || prefetchedUrls.size >= 4) return;

    const url = eligibleProductUrl(anchor);
    if (!url || prefetchedUrls.has(url)) return;

    prefetchedUrls.add(url);
    const link = document.createElement('link');
    link.rel = 'prefetch';
    link.href = url;
    link.fetchPriority = 'low';
    document.head.appendChild(link);
  };

  const cancelPendingPrefetch = () => {
    window.clearTimeout(pendingTimer);
    pendingTimer = null;
    pendingAnchor = null;
  };

  const saveScrollPosition = (force = false) => {
    scrollSaveFrame = null;
    if (scrollPositionFrozen && !force) return;

    const position = { x: window.scrollX, y: window.scrollY };

    try {
      const currentState = window.history.state && typeof window.history.state === 'object'
        ? window.history.state
        : {};
      window.history.replaceState({ ...currentState, preaScrollPosition: position }, '');
      window.sessionStorage.setItem(scrollPositionKey, JSON.stringify(position));
    } catch (error) {
      // History state and storage can be unavailable in strict privacy modes.
    }
  };

  const scheduleScrollPositionSave = () => {
    if (scrollSaveFrame) return;
    scrollSaveFrame = window.requestAnimationFrame(saveScrollPosition);
  };

  const restoreHistoryPosition = () => {
    if (!document.documentElement.classList.contains('prea-history-navigation')) return;

    let position = window.history.state?.preaScrollPosition || null;
    try {
      if (!position) position = JSON.parse(window.sessionStorage.getItem(scrollPositionKey));
    } catch (error) {
      // Keep the position from history.state when storage is unavailable.
    }

    const targetX = Number.isFinite(position?.x) ? position.x : 0;
    const targetY = Number.isFinite(position?.y) ? position.y : 0;
    const previousScrollBehavior = document.documentElement.style.scrollBehavior;
    document.documentElement.style.scrollBehavior = 'auto';

    const applyPosition = () => window.scrollTo(targetX, targetY);
    const finishRestore = () => {
      applyPosition();
      document.documentElement.classList.add('prea-history-restored');
      document.documentElement.style.scrollBehavior = previousScrollBehavior;
      scrollPositionFrozen = false;
    };

    applyPosition();
    window.requestAnimationFrame(() => {
      applyPosition();
      window.requestAnimationFrame(finishRestore);
    });
  };

  restoreHistoryPosition();
  window.addEventListener('scroll', scheduleScrollPositionSave, { passive: true });

  document.addEventListener(
    'pointerover',
    (event) => {
      const anchor = event.target.closest?.('a[href]');
      if (!eligibleProductUrl(anchor) || anchor === pendingAnchor) return;

      cancelPendingPrefetch();
      pendingAnchor = anchor;
      pendingTimer = window.setTimeout(() => {
        prefetchProduct(anchor);
        cancelPendingPrefetch();
      }, 90);
    },
    { passive: true }
  );

  document.addEventListener(
    'pointerout',
    (event) => {
      if (!pendingAnchor || event.relatedTarget?.closest?.('a[href]') === pendingAnchor) return;
      cancelPendingPrefetch();
    },
    { passive: true }
  );

  document.addEventListener(
    'pointerdown',
    (event) => {
      saveScrollPosition(true);
      prefetchProduct(event.target.closest?.('a[href]'));
    },
    { capture: true, passive: true }
  );
  document.addEventListener(
    'click',
    (event) => {
      const anchor = event.target.closest?.('a[href]');
      if (!anchor || anchor.target === '_blank' || anchor.hasAttribute('download')) return;

      let url;
      try {
        url = new URL(anchor.href, window.location.href);
      } catch (error) {
        return;
      }

      if (url.origin !== window.location.origin) return;
      saveScrollPosition(true);
      scrollPositionFrozen = true;
    },
    { capture: true, passive: true }
  );
  document.addEventListener('focusin', (event) => prefetchProduct(event.target.closest?.('a[href]')), {
    passive: true
  });

  window.addEventListener('pageshow', (event) => {
    if (!event.persisted) return;

    // A BFCache restore should reveal the preserved page immediately and keep
    // the browser's saved scroll position intact.
    document.documentElement.classList.add('prea-history-navigation', 'prea-history-restored');
    document.documentElement.classList.remove(
      'prea-page-reload-overlay-active',
      'prea-page-reload-overlay-hiding'
    );
  });
})();
